/**
 * src/lib/idempotencyKeys.js — RC 1.49.1 P0-FIN (operator-locked, 2026-05-08).
 *
 * Action-scoped, per-(user, job) idempotency keys for generation
 * actions. Backed by sessionStorage so a retry from the same wizard
 * mount within `TTL_MS` reuses the SAME key — letting the existing
 * `credit_ledger.idempotency_key` partial unique index (migration
 * 004) deduplicate the second `charge_credits` RPC call and prevent
 * a second debit.
 *
 * Hard rails:
 *   • Keys are MINTED only when one isn't already cached for the
 *     given (user, job, action) tuple. Successive Generate clicks
 *     for the same intent return the SAME key — that's the whole
 *     point.
 *   • Keys are CLEARED only on successful completion of the action,
 *     so a stale key never bleeds into a future, intentionally-fresh
 *     generation.
 *   • Action-scoped: `cv_variant` and `cover_letter` keys never
 *     collide. The backend additionally prefixes the action name
 *     before passing to `credits_svc.charged(idempotency_key=…)`.
 *   • Falls back to crypto.randomUUID if available; otherwise a
 *     non-cryptographic but UUIDv4-shaped fallback. The DB unique
 *     index does not require RFC-4122 conformance — collision
 *     probability is negligible at our scale either way.
 *
 * NOT a billing surface, NOT a schema change, NOT a new endpoint.
 */

const PREFIX = 'ndor-idemp';

/** Session-scoped TTL. Slightly longer than the longest realistic
 *  LLM call (CV generation routinely runs 30-90s, occasionally up
 *  to ~2min on large source CVs) to ensure a retry after a 504
 *  reuses the same key. */
export const IDEMPOTENCY_TTL_MS = 10 * 60 * 1000;

function _storageKey(userId, jobId, action) {
  return `${PREFIX}:${action}:${userId || 'anon'}:${jobId || ''}`;
}

function _uuid() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch { /* fallthrough */ }
  // RFC-4122-shaped fallback (non-cryptographic, sufficient for
  // (user_id, idempotency_key) DB uniqueness at our scale).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Return the cached idempotency key for this (user, job, action),
 * or mint a fresh one and persist it. Always returns a non-empty
 * string.
 */
export function getOrMintIdempotencyKey(userId, jobId, action) {
  if (!action) return _uuid();
  const sk = _storageKey(userId, jobId, action);
  try {
    const raw = sessionStorage.getItem(sk);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (
        parsed && typeof parsed === 'object'
        && typeof parsed.key === 'string' && parsed.key.length > 0
        && typeof parsed.mintedAtMs === 'number'
        && (Date.now() - parsed.mintedAtMs) < IDEMPOTENCY_TTL_MS
      ) {
        return parsed.key;
      }
    }
  } catch { /* sessionStorage unavailable / corrupt — mint fresh */ }

  const fresh = _uuid();
  try {
    sessionStorage.setItem(
      sk,
      JSON.stringify({ key: fresh, mintedAtMs: Date.now() }),
    );
  } catch { /* non-fatal — still return the key for this call */ }
  return fresh;
}

/**
 * Drop the cached key for (user, job, action). Call on confirmed
 * success so the next legitimate generation gets a fresh key.
 */
export function clearIdempotencyKey(userId, jobId, action) {
  if (!action) return;
  try {
    sessionStorage.removeItem(_storageKey(userId, jobId, action));
  } catch { /* no-op */ }
}

/** Read-only inspection helper used by tests. */
export function peekIdempotencyKey(userId, jobId, action) {
  if (!action) return null;
  try {
    const raw = sessionStorage.getItem(_storageKey(userId, jobId, action));
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p?.key || null;
  } catch { return null; }
}
